import { useEffect, useRef, useState } from "react";
import { COLORS } from "@/lib/airep";

const hash = (s) => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);

function tryLayout(words, W, H, max, k) {
  const placed = [];
  const out = [];
  for (let i = 0; i < words.length; i += 1) {
    const w = words[i];
    let size = Math.round((14 + Math.pow(w.count / max, 0.8) * 58 * k) * Math.max(Math.min(1, W / 900), 0.55));
    let ok = false;
    while (size >= 12 && !ok) {
      const bw = Math.min(w.text.length * size * 0.66 + 16, W - 12);
      const bh = size * 1.2;
      for (let t = 0; t < 1600 && !ok; t += 1) {
        const a = t * 0.35 + (hash(w.text) % 10) * 0.1;
        const rad = 3 * t * 0.35;
        const x = W / 2 + rad * Math.cos(a) * 1.5;
        const y = H / 2 + rad * Math.sin(a) * 0.8;
        const box = { l: x - bw / 2, r: x + bw / 2, t: y - bh / 2, b: y + bh / 2 };
        if (box.l < 6 || box.r > W - 6 || box.t < 6 || box.b > H - 6) continue;
        if (!placed.some((p) => box.l < p.r && box.r > p.l && box.t < p.b && box.b > p.t)) { placed.push(box); out.push({ ...w, size, x, y, i }); ok = true; }
      }
      if (!ok) size = Math.floor(size * 0.85);
    }
    if (!ok) return null;
  }
  return out;
}

function layoutWords(words, W, H0) {
  const max = Math.max(...words.map((w) => w.count), 1);
  const k = Math.min(1, Math.sqrt(18 / words.length));
  let H = H0;
  for (let n = 0; n < 12; n += 1) {
    const items = tryLayout(words, W, H, max, k);
    if (items) return { items, H };
    H = Math.round(H * 1.2);
  }
  return { items: tryLayout(words, W, H * 2, max, k / 2) || [], H: H * 2 };
}

const PollBars = ({ slide, results, dark, palette = COLORS }) => {
  const total = Math.max(results.counts.reduce((a, b) => a + b, 0), 1);
  return (
    <div className="space-y-3 w-full" data-testid="results-poll">
      {slide.options.map((o, i) => {
        const pct = Math.round((results.counts[i] * 100) / total);
        return (
          <div key={i}>
            <div className={`flex justify-between text-sm sm:text-base mb-1 ${dark ? "text-slate-200" : "text-slate-700"}`}>
              <span className="font-medium truncate pr-3">{o}</span>
              <span className="font-mono">{results.counts[i]} · {pct}%</span>
            </div>
            <div className={`h-4 sm:h-6 rounded-full overflow-hidden ${dark ? "bg-white/10" : "bg-slate-100"}`}>
              <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${pct}%`, backgroundColor: palette[i % palette.length], boxShadow: dark ? `0 0 18px ${palette[i % palette.length]}66` : "none" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Scale = ({ results, dark, palette = COLORS }) => {
  const max = Math.max(...results.distribution, 1);
  return (
    <div className="flex flex-col items-center gap-6 w-full" data-testid="results-scale">
      <div className="text-center">
        <div className={`font-display font-extrabold text-6xl sm:text-7xl ${dark ? "text-[#E3FF00]" : "text-slate-900"}`}>{results.mean ?? "–"}</div>
        <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>media su 5 · {results.total} voti</div>
      </div>
      <div className="flex items-end gap-3 sm:gap-5 h-32 sm:h-40">
        {results.distribution.map((n, i) => (
          <div key={i} className="flex flex-col items-center gap-1 w-10 sm:w-14">
            <span className={`text-xs font-mono ${dark ? "text-slate-300" : "text-slate-600"}`}>{n}</span>
            <div className="w-full rounded-t-lg transition-[height] duration-700" style={{ height: `${(n / max) * 100}%`, minHeight: 4, backgroundColor: palette[i % palette.length] }} />
            <span className={`text-sm font-bold ${dark ? "text-white" : "text-slate-900"}`}>{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Cloud = ({ results, dark, palette = COLORS, height = 420 }) => {
  const ref = useRef(null);
  const [W, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  if (!results.words.length) return <Empty dark={dark} />;
  const { items, H } = layoutWords(results.words, W, Math.max(260, Math.min(height, W * 0.6)));
  return (
    <div ref={ref} className="relative w-full overflow-hidden" style={{ height: H }} data-testid="results-wordcloud">
      {items.map((w) => {
        const h = hash(w.text);
        const top = w.i === 0;
        return (
          <span key={w.text} className="airep-word font-display font-bold leading-none" data-testid={`cloud-word-${w.i}`}
            style={{ left: w.x, top: w.y, fontSize: w.size, "--dur": `${6 + (h % 50) / 10}s`, "--delay": `-${(h % 30) / 10}s`,
              color: top ? (dark ? (palette === COLORS ? "#E3FF00" : palette[0]) : "#0f172a") : palette[(h % (palette.length - 1)) + 1],
              textShadow: top && dark ? `0 0 24px ${palette === COLORS ? "#E3FF00" : palette[0]}88` : "none", opacity: 0.55 + 0.45 * (w.count / results.words[0].count) }}>{w.text}</span>
        );
      })}
    </div>
  );
};

const Open = ({ results, dark }) => {
  if (!results.answers.length) return <Empty dark={dark} />;
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 w-full" data-testid="results-open">
      {results.answers.map((a, i) => (
        <div key={i} className={`break-inside-avoid mb-3 rounded-2xl p-4 text-sm sm:text-base animate-in-up ${dark ? "bg-[#121820] border border-[#212d3b] text-slate-100" : "bg-white border border-slate-200 text-slate-800"}`}>{a.text}</div>
      ))}
    </div>
  );
};

const Empty = ({ dark }) => <div className={`text-center py-10 ${dark ? "text-slate-500" : "text-slate-400"}`}>In attesa delle prime risposte…</div>;

export const Results = ({ slide, results, dark = false, palette = COLORS }) => {
  if (!slide || !results) return null;
  if (slide.type === "poll") return <PollBars slide={slide} results={results} dark={dark} palette={palette} />;
  if (slide.type === "scale") return <Scale results={results} dark={dark} palette={palette} />;
  if (slide.type === "wordcloud") return <Cloud results={results} dark={dark} palette={palette} height={dark ? 520 : 360} />;
  return <Open results={results} dark={dark} />;
};

export const AirepLogo = ({ className = "h-9" }) => <img src="/airep-logo-full.svg" alt="AI-Report powered by mypasspartu" className={className} />;
